import { toast } from "react-toastify";
import { confirmAlert } from "../components/ui/confirmAlert";
import { Get, Send, Update, Remove, API } from "../services/service.api";

/**
 * Generic function to get data from an endpoint
 * @param {string} endpoint - The API endpoint to fetch data from
 * @param {Function} setFunction - The state setter to store the data
 * @param {string} token - The authorization token
 */
export async function handleGet(endpoint, setFunction, token = "") {
    try {
        const res = await Get(endpoint, token);
        if (res) {
            setFunction(res);
        }
    } catch (error) {
        toast.error("Erreur lors du chargement des données");
        console.error(error);
    }
}

/**
 * Activer une année scolaire spécifique
 * @param {string} id - ID de l'année scolaire
 * @param {string} ecoleId - ID de l'école
 * @param {string} token - Token d'autorisation
 * @param {Function} onSuccess - Callback si succès
 */
export async function handleSetActiveYear(id, ecoleId, token, onSuccess) {
    try {
        const data = await Update(`/ecole-annee/${id}/active/${ecoleId}`, {}, token);
        if (data.state === "success") {
            toast.success(data.message);
            if (onSuccess) onSuccess();
        } else {
            toast.error(data.message || "Erreur");
        }
    } catch (err) {
        toast.error("Erreur de connexion");
        console.error(err);
    }
}

/**
 * Mettre à jour la photo de profil d'un utilisateur
 * @param {File} file - Le fichier image sélectionné
 * @param {string} userId - L'ID de l'utilisateur
 * @param {string} token - Token d'autorisation
 * @param {Function} [onSuccess] - Callback si succès
 */
export async function handleUpdateProfilPicture(file, userId, token, onSuccess) {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
        const data = await Update(`/user/update/profil/${userId}`, formData, token, false);
        if (data.state === "success") {
            toast.success("Photo de profil mise à jour !");
            // Fetch fresh user data from DB to reflect the new profil field
            const freshRes = await Get(`/user/${userId}`, token);
            if (onSuccess) onSuccess(freshRes?.user ?? null);
        } else {
            toast.error("Erreur lors de la mise à jour");
        }
    } catch (err) {
        toast.error("Erreur de connexion");
        console.error(err);
    }
}

/**
 * Mettre à jour le logo d'une ecole
 * @param {File} file - Le fichier image sélectionné
 * @param {string} ecoleId - L'ID de l'ecole
 * @param {string} token - Token d'autorisation
 * @param {Function} [onSuccess] - Callback si succès
 */
export async function handleUpdateEcoleLogo(file, ecoleId, token, onSuccess) {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
        const data = await Update(`/ecole/update/logo/${ecoleId}`, formData, token, false);
        if (data.state === "success") {
            toast.success("Logo mis à jour !");
            const freshRes = await Get(`/ecole/${ecoleId}`, token);
            if (onSuccess) onSuccess(freshRes?.ecole ?? null);
        } else {
            toast.error("Erreur lors de la mise à jour");
        }
    } catch (err) {
        toast.error("Erreur de connexion");
        console.error(err);
    }
}

/**
 * Télécharger un export PDF
 * @param {string} type - Le type d'export (students, teachers, admins)
 * @param {string} ecoleId - ID de l'école
 * @param {string} token - Token d'autorisation
 * @param {Function} [setLoading] - Setter pour le loading state
 */
export async function handleDownloadCSV(type, ecoleId, token, setLoading = null) {
    if (setLoading) setLoading(true);
    try {
        const res = await fetch(`${API}/user/export/csv/${type}/${ecoleId}`, {
            method: "GET",
            headers: { "Authorization": `Bearer ${token}` }
        });

        if (res.ok) {
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `export_${type}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Export CSV généré avec succès !");
        } else {
            toast.error("Erreur lors de la génération du PDF.");
        }
    } catch (error) {
        toast.error("Erreur réseau lors de l'export.");
        console.error(error);
    } finally {
        if (setLoading) setLoading(false);
    }
}

/**
 * Generic function to post data to an endpoint
 * @param {Event} e - Optional event to prevent default
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The payload
 * @param {Function} [setLoading] - Optional state setter to manage loading state
 * @param {string} token - The authorization token
 * @param {boolean} stringify - Whether to stringify the payload
 * @returns {Promise<Object|null>} The response
 */
export async function handleSend(e, endpoint, data, setLoading = null, token = "", stringify = true) {
    if (e) e.preventDefault();
    if (setLoading) setLoading(true);
    try {
        const res = await Send(endpoint, data, stringify, token);
        if (res && res.state === "success") {
            toast.success(res.message || "Enregistrement réussi");
            return res;
        } else if (res && res.state === "warning") {
            toast.warning(res.message || res.error || "Erreur d'enregistrement");
        } else if (res && res.detail) {
            let errorMessage = "Erreur serveur";
            if (typeof res.detail === "string") {
                errorMessage = res.detail;
            } else if (Array.isArray(res.detail)) {
                errorMessage = res.detail[0].msg;
            } else if (res.detail && res.detail.error) {
                errorMessage = res.detail.error;
            }
            toast.error(errorMessage);
        } else {
            toast.success("Opération réussie");
            return res;
        }
    } catch (error) {
        toast.error("Erreur, impossible d'effectuer l'opération");
    } finally {
        if (setLoading) setLoading(false);
    }
    return null;
}

/**
 * Generic function to update data
 * @param {Event} e - Optional event to prevent default
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The payload
 * @param {Function} [setLoading] - Optional state setter to manage loading state
 * @param {string} token - The authorization token
 * @param {boolean} stringify - Whether to stringify the payload
 * @returns {Promise<Object|null>} The response
 */
export async function handleUpdate(e, endpoint, data, setLoading = null, token = "", stringify = true) {
    if (e) e.preventDefault();
    if (setLoading) setLoading(true);
    try {
        const res = await Update(endpoint, data, token, stringify);
        if (res && res.state === "success") {
            toast.success(res.message || "Mise à jour réussie");
            return res;
        } else if (res && res.state === "warning") {
            toast.info(res.message || "Aucune modification détectée");
            return res;
        } else if (res && res.error) {
            toast.error(res.message || res.error || "Erreur lors de la mise à jour");
        } else if (res && res.detail) {
            // Handle FastAPI errors
            let errorMessage = "Erreur serveur";
            if (typeof res.detail === "string") {
                errorMessage = res.detail;
            } else if (Array.isArray(res.detail)) {
                errorMessage = res.detail[0].msg;
            } else if (res.detail && res.detail.error) {
                errorMessage = res.detail.error;
            }
            toast.error(errorMessage);
        } else {
            toast.error("Échec de la mise à jour");
            return res;
        }
    } catch (error) {
        toast.error("Erreur, impossible d'effectuer la mise à jour");
        console.error(error);
    } finally {
        if (setLoading) setLoading(false);
    }
    return null;
}

/**
 * Generic function to delete data
 * @param {string} endpoint - The API endpoint
 * @param {Function} [fetchFn] - Optional callback function to refresh the list after deletion
 * @param {string} token - The authorization token
 * @returns {Promise<boolean>} True if deleted successfully
 */
export async function handleRemove(endpoint, fetchFn = null, token = "", options = {}) {
    let isConfirmed = true;
    if (!options.skipConfirm) {
        isConfirmed = await confirmAlert({
            title: options.title || "Suppression",
            message: options.message || "Voulez-vous vraiment supprimer cet élément ?",
            confirmText: options.confirmText || "Supprimer",
            type: options.type || "danger"
        });
    }

    if (isConfirmed) {
        try {
            const res = await Remove(endpoint, token);
            if (res && (res.state === "success" || res.status === "success")) {
                toast.success(res.message || "Suppression effectuée");
                if (fetchFn) fetchFn();
                return true;
            } else {
                toast.warn(res?.message || res?.detail?.error || "La suppression n'a pas pu être effectuée");
            }
        } catch (error) {
            toast.error("Erreur, impossible de supprimer");
            console.error(error);
        }
    }
    return false;
}

/**
 * Generic POST — envoie des données et transmet la réponse à un callback
 * @param {string} endpoint - L'endpoint API
 * @param {Object} data - Le payload à envoyer
 * @param {Function} [callback] - Callback appelé avec la réponse brute
 * @param {string} token - Token d'autorisation
 * @returns {Promise<Object|null>} La réponse brute
 */
export async function handlePost(endpoint, data, callback = null, token = "") {
    try {
        const res = await Send(endpoint, data, true, token);
        if (callback) callback(res);
        return res;
    } catch (error) {
        toast.error("Erreur, impossible d'effectuer l'opération");
        console.error(error);
        return null;
    }
}

/**
 * Generic PUT — met à jour des données et transmet la réponse à un callback
 * @param {string} endpoint - L'endpoint API
 * @param {Object} data - Le payload à envoyer
 * @param {Function} [callback] - Callback appelé avec la réponse brute
 * @param {string} token - Token d'autorisation
 * @returns {Promise<Object|null>} La réponse brute
 */
export async function handlePut(endpoint, data, callback = null, token = "") {
    try {
        const res = await Update(endpoint, data, token, true);
        if (callback) callback(res);
        return res;
    } catch (error) {
        toast.error("Erreur, impossible d'effectuer la mise à jour");
        console.error(error);
        return null;
    }
}


